/**
 * As-reported foreign-ledger VOCABULARY — SCRUM-367 (DS-026 S5, P0, decision D1).
 *
 * The dependency-free half of the as-reported ledger split. This file carries
 * ONLY the enumerated value-sets + their derived unions (mirrors
 * `reservation-type-internal-key.ts` / `revenue-distribution-method.ts`, ADR 0027
 * shared-kernel for cross-BC value objects) plus the single canonical LINE-ORDER
 * comparator that both consumers must agree on.
 *
 * Why the split (D1, REVISED at P0-RED): the money-CARRYING structs
 * (`AsReportedChargeLine` / `AsReportedLedger`, whose amounts are cents) and the
 * pure helpers over them (`checkAuthoritativeTotal` / `normalizeAsReportedLedger`)
 * live in `@artemis/validators`, because they reference the cents primitive and
 * `@artemis/validators` is where cents lives. `@artemis/domain` is a genuinely
 * DEPENDENCY-FREE kernel (`package.json` has no "dependencies"); it CANNOT import
 * `@artemis/validators` (validators depends on domain, never the reverse). So the
 * value-vocab lands here (dependency-free) and the schemas land in validators —
 * exactly the `RESERVATION_TYPE_INTERNAL_KEY_VALUES` ⇄ tax `z.enum(...)` precedent.
 *
 * TWO bounded contexts need this vocab and must NOT import each other's domain:
 *   - `booking`  — the Layer-1 `channel-remittance` snapshot arm persists an
 *     `AsReportedLedger`; its line `kind`/`remittedBy`/`provenance` sentinels are
 *     these unions, and the snapshot factory canonicalises line order via
 *     {@link compareAsReportedLineOrder} before hashing.
 *   - `channel`  — the NextPax ACL mapper (P4) derives `remittedBy` / `provenance`
 *     / `kind` per line and builds the `AsReportedLedger` handed to the booking
 *     ingest port.
 *
 * Boundary discipline (ADR 0011 / CLAUDE.md): pure TypeScript. No NestJS, no
 * Prisma, no Zod, no `new Date()`.
 */
/**
 * Who is responsible for remitting a given charge/tax line to the taxing
 * authority or to the PM (§9.2 / D5). Fail-safe sentinel `'undetermined'` is a
 * NON-NULL value (never silently `'pm'`/`'channel'`) so an ambiguous or
 * unreadable channel breakdown surfaces visibly rather than risking a
 * double-/under-remittance. Frozen source-of-truth list; the union is derived.
 */
export const REMITTED_BY_VALUES = Object.freeze([
    'channel',
    'pm',
    'undetermined',
]);
/**
 * Provenance of an as-reported charge line (§9.3a.5): whether the line is the
 * channel's own itemised figure (`'channel-explicit'`), a derived per-category
 * projection we computed from a coarser channel total (`'derived-split'`, RESERVED
 * for the 369 tax-split enrichment — never fabricated in Phase-1), or an
 * unclassifiable line we transcribe verbatim rather than drop (`'unmapped'`).
 * Frozen source-of-truth list; the union is derived.
 */
export const AS_REPORTED_PROVENANCE_VALUES = Object.freeze([
    'channel-explicit',
    'derived-split',
    'unmapped',
]);
/**
 * The coarse `kind` bucket of an as-reported charge line (§9.4). Phase-1 is
 * coarse-`kind`-only — the channel's own `code` rides verbatim alongside. The
 * ARRAY ORDER is load-bearing: it is the PRIMARY key of the canonical line sort
 * ({@link compareAsReportedLineOrder}) that makes a re-ingested ledger hash
 * identically regardless of the channel's line ordering. Frozen source-of-truth
 * list; the union is derived.
 */
export const AS_REPORTED_CHARGE_KIND_VALUES = Object.freeze([
    'rent',
    'fee',
    'tax',
    'commission',
    'discount',
]);
/**
 * Canonical line-order comparator (§9.3a.3) — the SINGLE source of truth for the
 * order in which as-reported lines are persisted, so a re-ingest of the same
 * reservation (whatever order the channel sends its lines in) yields an identical
 * `contentHash` (re-ingest idempotency). Order key:
 *   1. `kind`, by its position in {@link AS_REPORTED_CHARGE_KIND_VALUES}
 *      (rent → fee → tax → commission → discount),
 *   2. then `code`, lexicographically (byte order — no locale, deterministic),
 *   3. then the caller relies on a STABLE sort for the original-index tiebreak
 *      (ECMAScript-2019 mandates `Array.prototype.sort` stability; Node ≥ 20).
 *
 * Both the booking snapshot factory (`charges-snapshot.factory.ts`, which cannot
 * import the validators runtime) and the validators `normalizeAsReportedLedger`
 * sort by THIS comparator, so the two producers can never drift. Pure, total,
 * side-effect-free; reads only `kind`/`code`.
 */
export function compareAsReportedLineOrder(a, b) {
    const kindDelta = AS_REPORTED_CHARGE_KIND_VALUES.indexOf(a.kind) -
        AS_REPORTED_CHARGE_KIND_VALUES.indexOf(b.kind);
    if (kindDelta !== 0) {
        return kindDelta;
    }
    if (a.code === b.code) {
        return 0;
    }
    return a.code < b.code ? -1 : 1;
}
//# sourceMappingURL=as-reported-ledger.js.map